import React, { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Group, Text, TextInput, Tooltip } from '@mantine/core';
import { IconFilter, IconX } from '@tabler/icons-react';
import { isEntityFieldFilterActive } from '../../utils/entityFieldFilterUtils';

export interface EntityFieldFilterInputProps {
    value: string;
    onChange: (value: string) => void;
    /** Shown below the input when filtering (e.g. from countEntityFieldFilterMatches). */
    matchCount?: number;
    totalCount?: number;
    /** Optional test id / aria override */
    'data-testid'?: string;
}

const FILTER_TOOLTIP =
    'Filter fields as you type. Matches field labels and current values; non-matching fields are dimmed and empty sections are hidden. Type * alone to show required fields and any label or value containing *.';

/**
 * Compact filter input for entity detail view and upsert forms (dashboard-style dim/hide).
 */
export const EntityFieldFilterInput: React.FC<EntityFieldFilterInputProps> = ({
    value,
    onChange,
    matchCount,
    totalCount,
    'data-testid': dataTestId = 'entity-field-filter-input',
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(value.trim().length > 0);
    }, [value]);

    const hasValue = value.length > 0;
    const filterActive = isEntityFieldFilterActive(value);
    const showCount =
        filterActive &&
        typeof matchCount === 'number' &&
        typeof totalCount === 'number';

    return (
        <>
            <style>{`
                .entity-field-filter-dimmed {
                    opacity: 0.36;
                    filter: grayscale(1) saturate(0.1);
                    font-size: 0.91em;
                    line-height: 1.12;
                    transform: scaleY(0.88) scaleX(0.97);
                    transform-origin: top left;
                    transition:
                        opacity 150ms ease,
                        filter 150ms ease,
                        transform 150ms ease,
                        font-size 150ms ease,
                        line-height 150ms ease;
                    pointer-events: none;
                }
                .entity-field-filter-dimmed .mantine-Input-input,
                .entity-field-filter-dimmed .mantine-Textarea-input,
                .entity-field-filter-dimmed .mantine-Select-input {
                    min-height: 1.85rem;
                    padding-top: 0.3rem;
                    padding-bottom: 0.3rem;
                    line-height: 1.12;
                }
                .entity-field-filter-wrap {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .entity-field-filter-input {
                    width: 220px;
                    max-width: min(92vw, 420px);
                    transition: width 200ms ease;
                }
                .entity-field-filter-input-expanded {
                    width: min(360px, 92vw);
                }
            `}</style>
            <Tooltip label={FILTER_TOOLTIP} multiline w={300} withArrow openDelay={400}>
                <Box className="entity-field-filter-wrap">
                    <TextInput
                        ref={inputRef}
                        data-testid={dataTestId}
                        aria-label="Filter fields"
                        placeholder="Filter fields…"
                        value={value}
                        onChange={(e) => onChange(e.currentTarget.value)}
                        size={expanded ? 'sm' : 'xs'}
                        leftSection={<IconFilter size={expanded ? 16 : 14} stroke={1.75} />}
                        rightSection={
                            hasValue ? (
                                <Group gap={4} wrap="nowrap" pr="xs">
                                    <Tooltip label="Clear filter">
                                        <ActionIcon
                                            variant="subtle"
                                            size={expanded ? 'sm' : 'xs'}
                                            color="gray"
                                            onClick={() => onChange('')}
                                            aria-label="Clear filter"
                                        >
                                            <IconX size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            ) : undefined
                        }
                        rightSectionWidth={hasValue ? 36 : undefined}
                        rightSectionPointerEvents="auto"
                        className={`entity-field-filter-input${expanded ? ' entity-field-filter-input-expanded' : ''}`}
                        styles={{
                            input: {
                                transition: 'font-size 180ms ease, padding 180ms ease, min-height 180ms ease',
                            },
                        }}
                    />
                    {showCount ? (
                        <Text
                            size="xs"
                            c="dimmed"
                            mt={6}
                            data-testid="entity-field-filter-count"
                            aria-live="polite"
                        >
                            Showing {matchCount} of {totalCount} field{totalCount === 1 ? '' : 's'}
                        </Text>
                    ) : null}
                </Box>
            </Tooltip>
        </>
    );
};
